import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, GraduationCap, ShieldCheck, MessageSquare, ArrowRight, Search } from 'lucide-react';

interface UserHelpCardProps {
  title: string;
  subtitle: string;
  actionLabel: string;
  onAction: () => void;
  icon: React.ReactNode;
}

const UserHelpCard: React.FC<UserHelpCardProps> = ({ title, subtitle, actionLabel, onAction, icon }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">{icon}</div>
      <button
        onClick={onAction}
        className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
      >
        {actionLabel} <ArrowRight size={14} />
      </button>
    </div>
    <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
  </div>
);

export const UserHelpView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in">
      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-800">What should I do now?</h2>
        <p className="text-slate-500 mt-2">
          Start with pending actions, complete your training, then review your assigned tools and submit feedback.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Priority actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <UserHelpCard
            title="Clear pending actions"
            subtitle="Check your pending tasks and complete them first."
            actionLabel="Open Portal"
            onAction={() => navigate('/portal')}
            icon={<CheckCircle2 size={18} />}
          />
          <UserHelpCard
            title="Complete assigned training"
            subtitle="Finish required training modules for your assigned tools."
            actionLabel="Go to My Tools"
            onAction={() => navigate('/portal')}
            icon={<GraduationCap size={18} />}
          />
          <UserHelpCard
            title="Request missing access"
            subtitle="Submit access requests for tools you need to do your job."
            actionLabel="Request access"
            onAction={() => navigate('/portal')}
            icon={<ShieldCheck size={18} />}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Best practices</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UserHelpCard
            title="Leave useful feedback"
            subtitle="Rate satisfaction and proficiency after real usage to improve tool decisions."
            actionLabel="Add feedback"
            onAction={() => navigate('/portal')}
            icon={<MessageSquare size={18} />}
          />
          <UserHelpCard
            title="Use filters to find tools faster"
            subtitle="Search and filter tools before sending new requests."
            actionLabel="Browse tools"
            onAction={() => navigate('/portal')}
            icon={<Search size={18} />}
          />
        </div>
      </section>
    </div>
  );
};
